/**
 * Chama o serviço de Recompra (`services/recompra`, ver README de lá).
 *
 * Same-origin: em produção o nginx do BI roteia `/api/recompra/` para o processo
 * em 127.0.0.1:8102 e injeta o `X-Token` nas escritas — o browser nunca vê o token.
 * Mesmo desenho do Buy Box.
 */

import type {
  ItemPedido,
  ModoAtualizacao,
  ParamImportacao,
  PayloadImportacao,
  PayloadRecompra,
  QtdImportacao,
  StatusRecompra,
} from './tipos'

export class RecompraApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'RecompraApiError'
    this.status = status
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, {
      credentials: 'include',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers || {}),
      },
    })
  } catch {
    throw new Error(
      'Serviço de Recompra inacessível. Se acabou de ser activado, confirme o proxy ' +
        'e o processo fiesta-recompra.',
    )
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as {
      erro?: string
      detail?: string | { msg?: string }
      message?: string
    } | null
    const detail =
      typeof corpo?.detail === 'string'
        ? corpo.detail
        : corpo?.detail && typeof corpo.detail === 'object'
          ? corpo.detail.msg
          : undefined
    throw new RecompraApiError(
      resp.status,
      corpo?.erro ||
        detail ||
        corpo?.message ||
        `Falha na chamada ao serviço de Recompra (HTTP ${resp.status}).`,
    )
  }
  return resp.json() as Promise<T>
}

export function fetchRecompra(dias = 180): Promise<PayloadRecompra> {
  return api<PayloadRecompra>(`/api/recompra/dados?dias=${dias}`)
}

export function fetchRecompraStatus(): Promise<StatusRecompra> {
  return api<StatusRecompra>('/api/recompra/status')
}

/**
 * Dispara a atualização: consulta o Tiny (saldo de hoje) e recalcula. Volta na hora;
 * o andamento chega pelo /status. `rapido` puxa só os SKUs com giro (~15 min);
 * `completo` puxa todos os SKUs simples (~45 min). O Tiny limita a 120 consultas/min.
 */
export function dispararAtualizacao(dias = 180, modo: ModoAtualizacao = 'rapido') {
  return api<{ ok: boolean; mensagem: string }>(
    `/api/recompra/atualizar?dias=${dias}&tiny=1&modo=${modo}`,
    { method: 'POST' },
  )
}

/** Marca SKU ou fornecedor como fora de linha. Grava só na collection da tela. */
export function marcarDescontinuado(
  tipo: 'produto' | 'fornecedor',
  valor: string,
  nome?: string,
) {
  return api('/api/recompra/descontinuados', {
    method: 'POST',
    body: JSON.stringify({ tipo, valor, nome }),
  })
}

export function desmarcarDescontinuado(id: string) {
  return api<{ removidos: number }>(
    `/api/recompra/descontinuados/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}

export function salvarItemPedido(item: {
  sku: string
  nome?: string | null
  forn?: string | null
  qtd: number
  sug: number
}) {
  return api<ItemPedido>(`/api/recompra/pedido/${encodeURIComponent(item.sku)}`, {
    method: 'PUT',
    body: JSON.stringify({
      nome: item.nome || '',
      forn: item.forn || '',
      qtd: item.qtd,
      sug: item.sug,
    }),
  })
}

export function removerItemPedido(id: string) {
  return api<{ removidos: number }>(`/api/recompra/pedido/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function esvaziarPedido() {
  return api<{ removidos: number }>('/api/recompra/pedido/esvaziar', { method: 'POST' })
}

// ------------------------------------------------------------ simulação de importação
// Payload calculado junto com a base; parâmetros e quantidades vêm do banco
// (`recompra_importacao`). Nada vai para o Tiny.

export function fetchImportacao(dias = 180): Promise<PayloadImportacao> {
  return api<PayloadImportacao>(`/api/recompra/importacao?dias=${dias}`)
}

export function salvarParamImportacao(p: ParamImportacao) {
  return api<ParamImportacao>('/api/recompra/importacao/param', {
    method: 'PUT',
    body: JSON.stringify({ meses: p.meses, lead: p.lead, base: p.base }),
  })
}

export function salvarQtdImportacao(sku: string, qtd: number) {
  return api<QtdImportacao>(`/api/recompra/importacao/${encodeURIComponent(sku)}`, {
    method: 'PUT',
    body: JSON.stringify({ qtd }),
  })
}

export function removerQtdImportacao(id: string) {
  return api<{ removidos: number }>(`/api/recompra/importacao/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export function esvaziarImportacao() {
  return api<{ removidos: number }>('/api/recompra/importacao/esvaziar', { method: 'POST' })
}
